import type { PortfolioItem } from "@/types/models"

export interface DiscountInfo {
  isActive: boolean
  isUpcoming: boolean
  isExpired: boolean
  discountAmount?: number
  discountPercentage?: number
  newPrice: number
  oldPrice: number
  endsAt?: Date
  startsAt?: Date
}

/**
 * Checks if a flash has an active discount and returns discount information
 */
export function getDiscountInfo(item: PortfolioItem): DiscountInfo | null {
  if (!item.discount) {
    return null
  }

  const now = new Date()
  const startsAt = item.discount.discountStartsAt instanceof Date 
    ? item.discount.discountStartsAt 
    : (item.discount.discountStartsAt as any)?.toDate?.() || new Date(item.discount.discountStartsAt)
  
  const endsAt = item.discount.discountEndsAt instanceof Date 
    ? item.discount.discountEndsAt 
    : (item.discount.discountEndsAt as any)?.toDate?.() || new Date(item.discount.discountEndsAt)

  const isActive = now >= startsAt && now <= endsAt
  const isUpcoming = now < startsAt
  const isExpired = now > endsAt

  return {
    isActive,
    isUpcoming,
    isExpired,
    discountAmount: item.discount.absoluteAmount,
    discountPercentage: item.discount.procentualAccount,
    newPrice: item.discount.newPrice,
    oldPrice: item.discount.oldPrice,
    endsAt,
    startsAt
  }
}

/**
 * Formats discount display text for badges/pills
 */
export function formatDiscountBadge(discountInfo: DiscountInfo): string {
  if (discountInfo.discountPercentage) {
    return `-${discountInfo.discountPercentage}%`
  }
  if (discountInfo.discountAmount) {
    return `-${discountInfo.discountAmount}€`
  }
  // Calculate percentage from prices if not provided
  const calculatedPercentage = Math.round(
    ((discountInfo.oldPrice - discountInfo.newPrice) / discountInfo.oldPrice) * 100
  )
  return `-${calculatedPercentage}%`
}

/**
 * Formats price display with discount information
 */
export function formatPriceWithDiscount(discountInfo: DiscountInfo): {
  currentPrice: string
  originalPrice: string
} {
  return {
    currentPrice: `${discountInfo.newPrice}€`,
    originalPrice: `${discountInfo.oldPrice}€`
  }
}

